import { CardContainer } from "../../components/Card/CardContainer";
import { useNavigate } from "react-router-dom";
import PageTitleNested from "../../components/PageTitle/PageTitleNested";
import BreadCrumb from "../../components/BreadCrumb/BreadCrumb";
import { JSX, useMemo, useState } from "react";
import { Divider, message } from "antd";
import StepAntd from "../../components/StepAntd/StepAntd";
import { useLocalStorage } from "../../hook/useLocalStorage";
import { resolveShopTierConfig } from "../ShopTier/config";
import { generateShopTierMockData } from "../ShopTier/mockData";
import Step1, { StartFromOption } from "./CreateShopTierBenefitStep/Step1";
import Step2, { Step2Result } from "./CreateShopTierBenefitStep/Step2";
import Step3 from "./CreateShopTierBenefitStep/Step3";

function CreateShopTierBenefit(): JSX.Element {
  const navigate = useNavigate();
  const [company] = useLocalStorage("profile", null);
  const config = resolveShopTierConfig(company?.company);
  const mockData = generateShopTierMockData(config);

  const nextYears = useMemo(() => {
    const years = mockData.configYears
      .map((y) => parseInt(y.year.replace(/\D/g, ""), 10))
      .filter((n) => !Number.isNaN(n));
    const latest = years.length ? Math.max(...years) : new Date().getFullYear() + 543;
    return Array.from({ length: 5 }, (_, i) => ({
      label: `ปี ${latest + i}`,
      value: `${latest + i}`,
    }));
  }, [mockData.configYears]);

  const [year, setYear] = useState<string>("2570");
  const [startFrom, setStartFrom] = useState<string>("copy");
  const [step, setStep] = useState<number>(0);
  const [step2Result, setStep2Result] = useState<Step2Result | null>(null);
  const stepItems = [
    { title: "เลือกปี" },
    { title: "แก้ไขสิทธิประโยชน์" },
    { title: "ตรวจสอบ" },
  ];

  const startFromOptions: StartFromOption[] = [
    {
      label: `คัดลอกจากปี ${new Date().getFullYear() + 543}`,
      description:
        "ได้ทุกระดับพร้อมรายการสิทธิประโยชน์และ % Rebate มาเป็นจุดเริ่ม แล้วแก้เฉพาะจุดที่เปลี่ยน",
      value: "copy",
    },
    {
      label: "สร้างใหม่ทั้งหมด",
      description:
        "ได้โครงระดับเปล่า ไม่มีรายการสิทธิประโยชน์ ต้องพิมพ์เองทุกระดับ ใช้เมื่อโปรแกรมปีใหม่ต่างจากเดิมทั้งชุด",
      value: "new",
    },
  ];

  const goNext = () => setStep((s) => Math.min(s + 1, stepItems.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const currentYearLabel = `ปี ${new Date().getFullYear() + 543}`;
  const sourceTiers = mockData.configYears.find((y) => y.year === currentYearLabel)?.tiers || [];

  const stepsComponents = [
    <Step1
      key={0}
      year={year}
      setYear={setYear}
      nextYears={nextYears}
      startFrom={startFrom}
      setStartFrom={setStartFrom}
      startFromOptions={startFromOptions}
      onNext={goNext}
    />,
    <Step2
      key={1}
      mode="create"
      config={config}
      sourceTiers={sourceTiers}
      startFrom={startFrom}
      onNext={(result) => {
        setStep2Result(result);
        goNext();
      }}
      onBack={goBack}
    />,
    <Step3
      key={2}
      mode="create"
      config={config}
      year={year}
      data={step2Result}
      sourceTiers={sourceTiers}
      startFrom={startFrom}
      onBack={goBack}
      onConfirm={() => message.info("ฟังก์ชันนี้อยู่ระหว่างการพัฒนา")}
    />,
  ];

  return (
    <CardContainer>
      <PageTitleNested
        title={`สร้างชุดสิทธิประโยชน์ปีใหม่`}
        showBack
        onBack={() => (step === 0 ? navigate(-1) : goBack())}
        customBreadCrumb={
          <BreadCrumb
            data={[
              { text: "ตั้งค่าระดับ", path: -1 },
              { text: `สร้างชุดสิทธิประโยชน์ปีใหม่`, path: window.location.pathname },
            ]}
          />
        }
        extra={<StepAntd current={step} items={stepItems} />}
      />
      <Divider />
      {stepsComponents[step]}
    </CardContainer>
  );
}

export default CreateShopTierBenefit;
